import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import {
  HouseCreditTransaction,
  IHouseCreditTransaction,
} from '../models/house-credit-transaction.model';
import { IMovement } from '../models/movement.model';

@Injectable({
  providedIn: 'root'
})
export class HouseCreditTransactionService {

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    private toastService: ToastService,
  ) {}

  /*
   █████╗ ██████╗ ██████╗ ██╗  ██╗   ██╗
  ██╔══██╗██╔══██╗██╔══██╗██║  ╚██╗ ██╔╝
  ███████║██████╔╝██████╔╝██║   ╚████╔╝
  ██╔══██║██╔═══╝ ██╔═══╝ ██║    ╚██╔╝
  ██║  ██║██║     ██║     ███████╗██║
  ╚═╝  ╚═╝╚═╝     ╚═╝     ╚══════╝╚═╝

  */
  // region apply
  async apply(
    neighborhoodId: string,
    streetBuildingId: string,
    houseFlatId: string,
    movementId: string
  ): Promise<HouseCreditTransaction | undefined> {
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/streets-buildings/${streetBuildingId}/houses-flats/${houseFlatId}/credits/apply/${movementId}`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ movement: IMovement; houseCreditTransaction: IHouseCreditTransaction; creditBalance: number }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ movement: IMovement; houseCreditTransaction: IHouseCreditTransaction; creditBalance: number }>>(
            urlApi,
            JSON.stringify({}),
            {
              headers: {
                Authorization: token.toString(),
                'Content-Type': 'application/json',
              },
            }
          ).subscribe({
            next: (response) => resolve(
              new ServerResponse<{ movement: IMovement; houseCreditTransaction: IHouseCreditTransaction; creditBalance: number }>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if (response.statusCode === 201 && response.data) {
        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white',
        });
        return new HouseCreditTransaction(response.data.houseCreditTransaction);
      } else {
        this.toastService.show({
          header: 'Error',
          body: response.message,
          classname: 'bg-danger text-white',
        });
      }
    } catch (error) {
      console.error(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white',
      });
    }

    return undefined;
  }

  /*
  ██████╗ ███████╗██████╗  ██████╗ ███████╗██╗████████╗
  ██╔══██╗██╔════╝██╔══██╗██╔═══██╗██╔════╝██║╚══██╔══╝
  ██║  ██║█████╗  ██████╔╝██║   ██║███████╗██║   ██║
  ██║  ██║██╔══╝  ██╔═══╝ ██║   ██║╚════██║██║   ██║
  ██████╔╝███████╗██║     ╚██████╔╝███████║██║   ██║
  ╚═════╝ ╚══════╝╚═╝      ╚═════╝ ╚══════╝╚═╝   ╚═╝

  */
  // region deposit
  async deposit(
    neighborhoodId: string,
    streetBuildingId: string,
    houseFlatId: string,
    data: { amount: number; notes: string }
  ): Promise<HouseCreditTransaction | undefined> {
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/streets-buildings/${streetBuildingId}/houses-flats/${houseFlatId}/credits/deposit`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ houseCreditTransaction: IHouseCreditTransaction }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ houseCreditTransaction: IHouseCreditTransaction }>>(
            urlApi,
            JSON.stringify(data),
            {
              headers: {
                Authorization: token.toString(),
                'Content-Type': 'application/json',
              },
            }
          ).subscribe({
            next: (response) => resolve(
              new ServerResponse<{ houseCreditTransaction: IHouseCreditTransaction }>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if (response.statusCode === 201 && response.data) {
        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white',
        });
        return new HouseCreditTransaction(response.data.houseCreditTransaction);
      } else {
        this.toastService.show({
          header: 'Error',
          body: response.message,
          classname: 'bg-danger text-white',
        });
      }
    } catch (error) {
      console.error(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white',
      });
    }

    return undefined;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  // region getAll
  async getAll(
    neighborhoodId: string,
    streetBuildingId: string,
    houseFlatId: string
  ): Promise<{ creditBalance: number; houseCreditTransactions: HouseCreditTransaction[] } | undefined> {
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/streets-buildings/${streetBuildingId}/houses-flats/${houseFlatId}/credits`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ creditBalance: number; houseCreditTransactions: IHouseCreditTransaction[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ creditBalance: number; houseCreditTransactions: IHouseCreditTransaction[] }>>(
            urlApi,
            {
              headers: {
                Authorization: token.toString(),
              },
            }
          ).subscribe({
            next: (response) => resolve(
              new ServerResponse<{ creditBalance: number; houseCreditTransactions: IHouseCreditTransaction[] }>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if (response.statusCode === 200 && response.data) {
        return {
          creditBalance: response.data.creditBalance,
          houseCreditTransactions: response.data.houseCreditTransactions.map(
            (t) => new HouseCreditTransaction(t)
          ),
        };
      } else {
        this.toastService.show({
          header: 'Error',
          body: response.message,
          classname: 'bg-danger text-white',
        });
      }
    } catch (error) {
      console.error(error);
    }

    return undefined;
  }

  /*
  ██████╗ ███████╗███████╗██╗   ██╗███╗   ██╗██████╗
  ██╔══██╗██╔════╝██╔════╝██║   ██║████╗  ██║██╔══██╗
  ██████╔╝█████╗  █████╗  ██║   ██║██╔██╗ ██║██║  ██║
  ██╔══██╗██╔══╝  ██╔══╝  ██║   ██║██║╚██╗██║██║  ██║
  ██║  ██║███████╗██║     ╚██████╔╝██║ ╚████║██████╔╝
  ╚═╝  ╚═╝╚══════╝╚═╝      ╚═════╝ ╚═╝  ╚═══╝╚═════╝

  */
  // region refund
  async refund(
    neighborhoodId: string,
    streetBuildingId: string,
    houseFlatId: string,
    data: { amount: number; notes: string }
  ): Promise<HouseCreditTransaction | undefined> {
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/streets-buildings/${streetBuildingId}/houses-flats/${houseFlatId}/credits/refund`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ houseCreditTransaction: IHouseCreditTransaction }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ houseCreditTransaction: IHouseCreditTransaction }>>(
            urlApi,
            JSON.stringify(data),
            {
              headers: {
                Authorization: token.toString(),
                'Content-Type': 'application/json',
              },
            }
          ).subscribe({
            next: (response) => resolve(
              new ServerResponse<{ houseCreditTransaction: IHouseCreditTransaction }>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if (response.statusCode === 201 && response.data) {
        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white',
        });
        return new HouseCreditTransaction(response.data.houseCreditTransaction);
      } else {
        this.toastService.show({
          header: 'Error',
          body: response.message,
          classname: 'bg-danger text-white',
        });
      }
    } catch (error) {
      console.error(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white',
      });
    }

    return undefined;
  }

}
